import { ENDPOINT } from '~/constants/endpoints';

import {
  buildResponseValidationReport,
  type KyRequestOptions,
} from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { type SuppliersPriorityResponse } from '../useQuerySuppliersPriority/types';
import { zodSuppliersPriorityResponseSchema } from '../useQuerySuppliersPriority/validations';

import { type PutSuppliersPriorityPayload } from './types';

/**
 * Replaces the supplier priority order.
 *
 * high_priority: ordered list of data_exchange_ids for high-priority slots.
 * regular: remaining data_exchange_ids in regular-priority order.
 *
 * Returns the updated priority list on success (HTTP 200).
 *
 * Validates strictly: `onSuccess` caches this response as trusted data.
 *
 * @param payload - The new priority assignment.
 * @param options - Optional ky request options (e.g., abort signal).
 * @returns The updated suppliers priority response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/put_suppliers_priority_suppliers_views_priority_put
 */
export const putSuppliersPriority = async (
  payload: PutSuppliersPriorityPayload,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.SUPPLIER.PUT_PRIORITY(), {
      ...options,
      json: payload,
    })
    .json();

  const parsed = zodSuppliersPriorityResponseSchema.safeParse(response);

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'put-suppliers-priority',
      message: 'put suppliers priority response has validation errors',
    });

    throw new Error(
      'Put suppliers priority response did not match the expected shape',
    );
  }

  return parsed.data satisfies SuppliersPriorityResponse;
};
